const FINAL_STATUSES = new Set([
  'paid',
  'confirmed',
  'rejected',
  'cancelled',
  'canceled',
]);

export function normalizeBookingStatus(raw) {
  if (!raw) return null;
  const status = String(raw).trim().toLowerCase();
  return status || null;
}

function getStatusFromBooking(bookingOrStatus) {
  if (typeof bookingOrStatus === 'string') {
    return normalizeBookingStatus(bookingOrStatus);
  }

  if (!bookingOrStatus || typeof bookingOrStatus !== 'object') {
    return null;
  }

  return normalizeBookingStatus(
    bookingOrStatus.effectiveStatus ||
      bookingOrStatus.currentStatus ||
      bookingOrStatus.bookingStatus ||
      bookingOrStatus.status ||
      bookingOrStatus.state
  );
}

function getDeadlineFromBooking(bookingOrStatus) {
  if (!bookingOrStatus || typeof bookingOrStatus !== 'object') return null;
  return (
    bookingOrStatus.expireAt ||
    bookingOrStatus.expiresAt ||
    bookingOrStatus.expirationAt ||
    bookingOrStatus.deadlineAt ||
    null
  );
}

function hasPaidPayment(bookingOrStatus) {
  if (!bookingOrStatus || typeof bookingOrStatus !== 'object') return false;
  return normalizeBookingStatus(bookingOrStatus.paymentStatus) === 'paid';
}

export function hasBookingExpired(bookingOrStatus, now = new Date()) {
  const status = getStatusFromBooking(bookingOrStatus);
  if (status === 'expired') return true;
  if (!status || FINAL_STATUSES.has(status) || hasPaidPayment(bookingOrStatus)) {
    return false;
  }

  const deadline = getDeadlineFromBooking(bookingOrStatus);
  if (!deadline) return false;

  const deadlineTime = new Date(deadline).getTime();
  const nowTime = now instanceof Date ? now.getTime() : new Date(now).getTime();

  return Number.isFinite(deadlineTime) && deadlineTime <= nowTime;
}

export function isCancelledStatus(status) {
  return status === 'cancelled' || status === 'canceled';
}

export function getEffectiveBookingStatus(bookingOrStatus, now = new Date()) {
  const status = getStatusFromBooking(bookingOrStatus);
  // Cancellation wins over a paid payment row. An admin refund leaves
  // status='Cancelled' with paymentStatus='Refunded', and legacy/incident rows
  // can still read 'Paid' — without this guard those render as Confirmed and
  // keep holding a placement. The API's own bookingState.js already resolves
  // them to 'Cancelled'; this keeps the two sides in agreement.
  if (isCancelledStatus(status)) return status;
  if (status === 'paid' || hasPaidPayment(bookingOrStatus)) return 'paid';
  if (hasBookingExpired(bookingOrStatus, now)) return 'expired';
  return status;
}

export function canReviewBooking(bookingOrStatus, now = new Date()) {
  return getEffectiveBookingStatus(bookingOrStatus, now) === 'pending';
}

export function canPayBooking(bookingOrStatus, now = new Date()) {
  return getEffectiveBookingStatus(bookingOrStatus, now) === 'approved';
}

// Brand-side cancel (T2B-323): allowed while the booking is still Pending or
// Approved and not yet paid. Mirrors the backend guard in
// bookingController.cancelBooking — keep the two in sync.
export function canCancelBooking(bookingOrStatus, now = new Date()) {
  if (hasPaidPayment(bookingOrStatus)) return false;
  const effective = getEffectiveBookingStatus(bookingOrStatus, now);
  return effective === 'pending' || effective === 'approved';
}
